import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DocumentTextIcon, SearchIcon } from '@heroicons/react/solid';
import { DocumentEmbedDialog } from '../../8_diagnosticDecision/components/DocumentEmbedDialog';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { useEmbeddedReportUrl } from '../../../../hooks/useEmbeddedReportUrl';
import { formatDateTime } from '../../../../utils/timestampUtils';
import { isSupportingDocStale } from '../../../../utils/supportingDocsVisibility';

export default function DocumentPreviewPanel({ patientId, clinicianId }) {
  const [selectedReport, setSelectedReport] = useState(null);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const { patientDataModel } = useContext(PatientDataContext);

  // Derive lastGenerated values for each supported report
  const ciLastGenerated = useMemo(() => patientDataModel?.documents?.clinicalinterviewReport?.lastGenerated || null, [patientDataModel]);
  const ssiLastGenerated = useMemo(() => patientDataModel?.documents?.semistructuredinterviewReport?.lastGenerated || null, [patientDataModel]);
  const qbTestLastGenerated = useMemo(() => patientDataModel?.documents?.qbtestReport?.lastGenerated || null, [patientDataModel]);
  const qbCheckLastGenerated = useMemo(() => patientDataModel?.documents?.qbcheckReport?.lastGenerated || null, [patientDataModel]);
  const documents = useMemo(() => patientDataModel?.documents || {}, [patientDataModel]);
  
  // Check if SSI reset is required (3 Borough / SLaM workflow)
  // When true, the SSI document should be hidden as it's pending re-editing
  const manageReports = useMemo(() => patientDataModel?.dataModel?.['6_manageReports'], [patientDataModel]);
  const isSSIResetRequired = useMemo(() => manageReports?.ssiResetRequired === true, [manageReports]);

  // Hide any supporting document whose report section has regressed to the
  // purple "AI outputs ready for review" state (was approved, then edits
  // regenerated paragraphs) - the rendered PDF may be stale. See
  // isSupportingDocStale for why we key off FirstApprovedAt, not Approved.
  const showCI = useMemo(() => Boolean(documents.clinicalinterviewReport) && !isSupportingDocStale(manageReports, 'clinicalinterviewReport'), [documents, manageReports]);
  // Hide SSI document if reset is required - the data is pending re-editing
  const showSSI = useMemo(() => Boolean(documents.semistructuredinterviewReport) && !isSSIResetRequired && !isSupportingDocStale(manageReports, 'semistructuredinterviewReport'), [documents, isSSIResetRequired, manageReports]);
  const showQbTest = useMemo(() => Boolean(documents.qbtestReport) && !isSupportingDocStale(manageReports, 'qbtestReport'), [documents, manageReports]);
  const showQbCheck = useMemo(() => Boolean(documents.qbcheckReport) && !isSupportingDocStale(manageReports, 'qbcheckReport'), [documents, manageReports]);

  // Hooks for prefetching and cache usage (idempotent)
  const { prefetch: prefetchCI } = useEmbeddedReportUrl(patientId, 'Clinical Interview', ciLastGenerated, clinicianId);
  const { prefetch: prefetchSSI } = useEmbeddedReportUrl(patientId, 'Semi-Structured Interview', ssiLastGenerated, clinicianId);
  const { prefetch: prefetchQbTest } = useEmbeddedReportUrl(patientId, 'QbTest', qbTestLastGenerated, clinicianId);
  const { prefetch: prefetchQbCheck } = useEmbeddedReportUrl(patientId, 'QbCheck', qbCheckLastGenerated, clinicianId);

  // Handler functions for the document dialog
  const handleOpenDocumentDialog = (reportStage) => {
    setSelectedReport(reportStage);
    setIsDocumentDialogOpen(true);
  };

  const handleCloseDocumentDialog = () => {
    setIsDocumentDialogOpen(false);
    setTimeout(() => setSelectedReport(null), 300);
  };

  // Prefetch both on panel visibility (mount)
  useEffect(() => {
    if (showCI) prefetchCI?.();
    if (showSSI) prefetchSSI?.();
    if (showQbTest) prefetchQbTest?.();
    if (showQbCheck) prefetchQbCheck?.();
  }, [prefetchCI, prefetchSSI, prefetchQbTest, prefetchQbCheck, showCI, showSSI, showQbTest, showQbCheck]);

  // Hide the panel entirely if nothing to show
  if (!showCI && !showSSI && !showQbTest && !showQbCheck) return null;

  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold mb-4">Supporting Documents</h3>
      <div className="flex gap-8">
        {/* Clinical Interview Report Preview */}
        {showCI && (
        <div className="flex flex-col items-center text-center">
          <div className="">
            <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
            <h4 className="font-medium text-gray-800 mb-1">Clinical Interview</h4>
            {ciLastGenerated && (
              <p className="text-xs text-gray-500 mt-1">
                Last updated on {formatDateTime(ciLastGenerated)}
              </p>
            )}
          </div>
          <button
            onClick={() => handleOpenDocumentDialog("Clinical Interview")}
            className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
            onMouseEnter={() => { try { prefetchCI?.(); } catch {} }}
            onFocus={() => { try { prefetchCI?.(); } catch {} }}
          >
            <SearchIcon className="h-4 w-4" />
            View
          </button>
        </div>
        )}

        {/* Semi-Structured Interview Report Preview */}
        {showSSI && (
        <div className="flex flex-col items-center text-center">
          <div className="">
            <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
            <h4 className="font-medium text-gray-800 mb-1">Semi-Structured Interview</h4>
            {ssiLastGenerated && (
              <p className="text-xs text-gray-500 mt-1">
                Last updated on {formatDateTime(ssiLastGenerated)}
              </p>
            )}
          </div>
          <button
            onClick={() => handleOpenDocumentDialog("Semi-Structured Interview")}
            className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
            onMouseEnter={() => { try { prefetchSSI?.(); } catch {} }}
            onFocus={() => { try { prefetchSSI?.(); } catch {} }}
          >
            <SearchIcon className="h-4 w-4" />
            View
          </button>
        </div>
        )}

        {/* QbCheck Results */}
        {showQbCheck && (
        <div className="flex flex-col items-center text-center">
          <div className="">
            <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
            <h4 className="font-medium text-gray-800 mb-1">QbCheck Results</h4>
            {qbCheckLastGenerated && (
              <p className="text-xs text-gray-500 mt-1">
                Last updated on {formatDateTime(qbCheckLastGenerated)}
              </p>
            )}
          </div>
          <button
            onClick={() => handleOpenDocumentDialog("QbCheck")}
            className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
            onMouseEnter={() => { try { prefetchQbCheck?.(); } catch {} }}
            onFocus={() => { try { prefetchQbCheck?.(); } catch {} }}
          >
            <SearchIcon className="h-4 w-4" />
            View
          </button>
        </div>
        )}

        {/* QbTest Results */}
        {showQbTest && (
        <div className="flex flex-col items-center text-center">
          <div className="">
            <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
            <h4 className="font-medium text-gray-800 mb-1">QbTest Results</h4>
            {qbTestLastGenerated && (
              <p className="text-xs text-gray-500 mt-1">
                Last updated on {formatDateTime(qbTestLastGenerated)}
              </p>
            )}
          </div>
          <button
            onClick={() => handleOpenDocumentDialog("QbTest")}
            className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
            onMouseEnter={() => { try { prefetchQbTest?.(); } catch {} }}
            onFocus={() => { try { prefetchQbTest?.(); } catch {} }}
          >
            <SearchIcon className="h-4 w-4" />
            View
          </button>
        </div>
        )}

        {/* Diagnostic Decision Report Preview */}
        {/* <div className="flex flex-col items-center text-center">
          <div className="">
            <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
            <h4 className="font-medium text-gray-800 mb-1">Diagnostic Decision</h4>
            <p className="text-sm text-gray-600">Report</p>
          </div>
          <button
            onClick={() => handleOpenDocumentDialog("Diagnostic Decision")}
            className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
          >
            <SearchIcon className="h-4 w-4" />
            View
          </button>
        </div> */}
      </div>

      {/* Document Dialog */}
      <DocumentEmbedDialog
        isOpen={isDocumentDialogOpen}
        onClose={handleCloseDocumentDialog}
        reportStage={selectedReport}
        patientId={patientId}
        clinicianId={clinicianId}
        lastGenerated={selectedReport === 'Clinical Interview' 
          ? ciLastGenerated 
          : selectedReport === 'Semi-Structured Interview' 
            ? ssiLastGenerated 
            : selectedReport === 'QbCheck'
              ? qbCheckLastGenerated
              : selectedReport === 'QbTest'
                ? qbTestLastGenerated
                : null}
      />
    </div>
  );
}